// API Reference source data (spec Appendix A + §5.3).
//
// The build must render the reference "generated from the same source data as
// the stubs so it can never drift" (Appendix A). Two sources feed it:
//   1. The method tables below, transcribed from Appendix A (the same source the
//      stub modules implement).
//   2. The action-group ID table, parsed LIVE from the real Hiwonder.py stub
//      (`?raw`), so it can never disagree with what runActionGroup accepts.

import hiwonderPy from "../pystubs/Hiwonder.py?raw";

export type ApiRow = {
  cls: string; // owning class / factory ("" for module-level functions)
  member: string; // method or member signature
  params: string;
  desc: string;
};

export type ApiSection = {
  id: string;
  module: string;
  note?: string;
  rows: ApiRow[];
};

export type ActionGroup = { id: number; name: string; durationMs: number };

// --- action-group table: parsed from the stub so it never drifts ------------

export function parseActionGroups(raw: string): ActionGroup[] {
  const start = raw.indexOf("ACTION_GROUPS = {");
  if (start === -1) return [];
  const end = raw.indexOf("\n}", start);
  const body = raw.slice(start, end === -1 ? raw.length : end);
  const re = /(\d+)\s*:\s*\(\s*"([^"]+)"\s*,\s*(\d+)\s*\)/g;
  const out: ActionGroup[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    out.push({ id: Number(m[1]), name: m[2], durationMs: Number(m[3]) });
  }
  return out;
}

export const ACTION_GROUPS: ActionGroup[] = parseActionGroups(hiwonderPy);

// --- method tables (Appendix A.1–A.4) --------------------------------------

export const API_SECTIONS: ApiSection[] = [
  {
    id: "hiwonder",
    module: "Hiwonder",
    rows: [
      { cls: "Tonybot", member: "Tonybot()", params: "—", desc: "Create the robot controller" },
      {
        cls: "Tonybot",
        member: "runActionGroup(group_id, repeat)",
        params: "group_id: int, valid action-group ID; repeat: int ≥ 0 (0 = loop forever)",
        desc: "Start a preset action group. Non-blocking — returns immediately while the robot moves",
      },
      {
        cls: "Tonybot",
        member: "stopActionGroup()",
        params: "—",
        desc: "Stop the currently running action group",
      },
      {
        cls: "Tonybot",
        member: "isRunning()",
        params: "— → bool",
        desc: "True while an action group is playing",
      },
      {
        cls: "Tonybot",
        member: "waitForStop()",
        params: "—",
        desc: "Block until the current finite action group finishes",
      },
      { cls: "Tonybot", member: "attachHead()", params: "—", desc: "Enable the head servo" },
      { cls: "Tonybot", member: "detachHead()", params: "—", desc: "Disable the head servo" },
      {
        cls: "Tonybot",
        member: "moveHeadAngle(angle)",
        params: "angle: number 0–180 (0 = right, 90 = centre, 180 = left)",
        desc: "Turn the head to an angle",
      },
      {
        cls: "Tonybot",
        member: "moveHead()",
        params: "(to be confirmed; simulated as centre-head)",
        desc: "Move the head servo",
      },
      {
        cls: "Tonybot",
        member: "moveServo(servo_id, angle, speed)",
        params:
          "servo_id: int 1–16; angle: int 0–1000 pulse units (~500 = centre); speed: travel time in ms, compulsory",
        desc: "Move one servo",
      },
      {
        cls: "Tonybot",
        member: "moveServos(a, b, c)",
        params: "3 parameters, structure not documented",
        desc: "Move several servos at once — not taught / not simulated",
      },
      {
        cls: "Tonybot",
        member: "setActionGroupSpeed(group_id, speed)",
        params: "2 parameters, units not documented",
        desc: "Change one group's playback speed — not taught / not simulated",
      },
      {
        cls: "Tonybot",
        member: "setAllActionGroupSpeed(speed)",
        params: "not documented",
        desc: "Change all groups' playback speed — not simulated",
      },
      {
        cls: "Tonybot",
        member: "getBatteryVolt()",
        params: "— → always -1",
        desc: "Battery query is not supported on the Mekaboy; the real robot returns -1, and so does the simulator",
      },
      { cls: "Buzzer (factory)", member: "Buzzer()", params: "—", desc: "Get the buzzer object" },
      {
        cls: "buzzer object",
        member: "playTone(freq, duration_ms, wait)",
        params: "freq: Hz; duration_ms: int; wait: bool, True = block until done",
        desc: "Play a tone",
      },
      {
        cls: "—",
        member:
          "Button, Digitaltube, IRrecv, Knob, Slider, LightSensor, SoilSensor, RaindropSensor, Fan_IO, WonderCam, UART, startMain",
        params: "—",
        desc: "Exist in the firmware; name-present, not simulated",
      },
    ],
  },
  {
    id: "hiwonder_iic",
    module: "Hiwonder_IIC",
    rows: [
      {
        cls: "IIC (factory)",
        member: "IIC()",
        params: "—",
        desc: "Get the I2C bus object (required by the sensors below)",
      },
      {
        cls: "bus object",
        member: "writeto(addr, data)",
        params: "I2C address, bytes",
        desc: "Raw I2C write (used by camera color configuration)",
      },
      {
        cls: "I2CSonar",
        member: "I2CSonar(iic)",
        params: "the bus object",
        desc: "Create the ultrasonic sensor (with built-in RGB LEDs)",
      },
      {
        cls: "I2CSonar",
        member: "getDistance()",
        params: "— → number",
        desc: "Distance in cm; 6553.5 = no echo / out of range (not 0)",
      },
      {
        cls: "I2CSonar",
        member: "setRGB(index, r, g, b)",
        params: "index: 0 = both, 1 = right, 2 = left; r,g,b: 0–255",
        desc: "Set the sensor's RGB LEDs",
      },
      {
        cls: "I2CSonar",
        member: "setRGBMode, setBreathingCycle, setRGBBreathingValue, startSymphony",
        params: "—",
        desc: "Name-present, not simulated in v1",
      },
      {
        cls: "ESP32S3Cam",
        member: "ESP32S3Cam(iic)",
        params: "the bus object",
        desc: "Create the camera",
      },
      {
        cls: "ESP32S3Cam",
        member: "read_color(color_id)",
        params:
          "color_id: int 1–5 (1 BLACK, 2 RED, 3 BLUE, 4 YELLOW, 5 GREEN) → (x, y, w, h) or None",
        desc: "Read the largest blob of a configured color",
      },
      {
        cls: "ESP32S3Cam",
        member: "read_face()",
        params: "—",
        desc: "Name-present, not simulated in v1",
      },
      { cls: "MPU (factory)", member: "MPU()", params: "—", desc: "Get the IMU object" },
      {
        cls: "imu object",
        member: "read_angle()",
        params:
          "— → tuple; [0] = X tilt in degrees (upright ≈ 90; forward fall −30…60; backward fall >120 or <−140)",
        desc: "Read orientation angles",
      },
      {
        cls: "—",
        member: "QMI8658, MP3, ASR, asr_module, AHTxx, WonderCam, WIFI_module, signedIntFromBytes",
        params: "—",
        desc: "Name-present, not simulated in v1",
      },
      {
        cls: "—",
        member: "asin(x), atan2(y, x), sqrt(x)",
        params: "—",
        desc: "Math helpers re-exported by the real module; fully working",
      },
    ],
  },
  {
    id: "microconcept",
    module: "MicroConcept_Humanoid",
    note: "The trainer's own module — runs verbatim on the stubs.",
    rows: [
      {
        cls: "Mekaboy",
        member: "Mekaboy()",
        params: "—",
        desc: "Create the robot; exposes .tony, .iic, .sonar, .cam, action-ID constants, and tuning attributes (BIAS, IMG_CENTER, …)",
      },
      {
        cls: "Mekaboy",
        member: "class constants",
        params:
          "BLACK=1, RED=2, BLUE=3, COL1=1, COL2=2, COL3=3; RawVarColorInfo, DefaultColorInfo, NicknameMap",
        desc: "Color IDs and HSV threshold tables",
      },
      {
        cls: "Mekaboy",
        member: "BasicMovement(movement='attention', numrepeat=0, duration=2)",
        params:
          "movement: forward/backward/smallforward/steerleft/steerright/smallsteerleft/smallsteerright/turnleft/turnright/attention/walkready/stopwalk; numrepeat: int, 0 = continuous, auto-wrapped in attention; duration: seconds",
        desc: "High-level basic movements",
      },
      {
        cls: "Mekaboy",
        member: "AdvanceMovement(movement='squatready', numrepeat=0, duration=2)",
        params:
          "movement: squatforward/stepover/stepdown/slideleft/slideright/squatready; numrepeat and duration as above",
        desc: "High-level advanced movements",
      },
      {
        cls: "Mekaboy",
        member: "HeadMovement(angle=90)",
        params: "angle: 0–180",
        desc: "Attach head → move → wait 1 s → detach",
      },
      {
        cls: "Mekaboy",
        member: "getAllDistance()",
        params: "—",
        desc: "Head-scan at 90/145/180/45/0° filling .Distance, .LDistance, .RDistance (in mm)",
      },
      {
        cls: "Mekaboy",
        member: "NavigateMaze(mindistance=150, numberturn=4)",
        params: "mindistance: threshold in mm; numberturn: turn repetitions",
        desc: "Autonomous maze navigation state machine",
      },
      {
        cls: "Mekaboy",
        member: "moveControl(x, strong_turn)",
        params: "blob x-centre, turn strength divisor",
        desc: "One line-following steering step",
      },
      {
        cls: "Mekaboy",
        member: "FollowLine(line_color=BLACK, turn_strong=90, duration=1000)",
        params: "line_color: color ID 1–5; turn_strong: steering divisor; duration: seconds",
        desc: "Camera line following for a fixed duration",
      },
      {
        cls: "Mekaboy",
        member: "CameraColorDefine(color_name, h_min, h_max, s_min, s_max, v_min, v_max)",
        params: "color_name: 'BLACK'/'COL1'/'[COL1]BLACK' nickname; HSV bounds 0–255 (H 0–180)",
        desc: "Redefine a color's HSV thresholds",
      },
      {
        cls: "Mekaboy",
        member: "CameraColorSetup()",
        params: "—",
        desc: "Push the color thresholds to the camera",
      },
      {
        cls: "Mekaboy",
        member: "sendColorConfiguration()",
        params: "— → 0 success / 1 error",
        desc: "Low-level threshold upload over I2C",
      },
      {
        cls: "rawColorInfo",
        member: "rawColorInfo(h_min, h_max, s_min, s_max, v_min, v_max)",
        params: "HSV bounds",
        desc: "Threshold holder used by the tables above",
      },
    ],
  },
  {
    id: "time",
    module: "time",
    note: "Virtualized MicroPython time — every function is bound to the simulation clock (§4.3).",
    rows: [
      { cls: "", member: "sleep(seconds)", params: "float/int", desc: "Pause (simulation clock)" },
      {
        cls: "",
        member: "sleep_ms(ms) / sleep_us(us)",
        params: "int",
        desc: "Pause in milliseconds / microseconds",
      },
      {
        cls: "",
        member: "ticks_ms() / ticks_us() / ticks_cpu()",
        params: "— → int",
        desc: "Millisecond / microsecond / CPU tick counter (simulation clock)",
      },
      {
        cls: "",
        member: "ticks_add(ticks, delta)",
        params: "int, int",
        desc: "Offset a ticks value with correct wrap-around",
      },
      {
        cls: "",
        member: "ticks_diff(t1, t2)",
        params: "int, int",
        desc: "Signed difference between two ticks values",
      },
      {
        cls: "",
        member: "time() / time_ns()",
        params: "—",
        desc: "Epoch seconds / nanoseconds (simulation clock)",
      },
      {
        cls: "",
        member: "gmtime([secs]) / localtime([secs]) / mktime(tuple)",
        params: "—",
        desc: "Calendar conversions",
      },
    ],
  },
];
